import { TICK_RATE, type MatchState } from "../game/simulation";

export type ArenaTeam = "A" | "B";

export interface ArenaPlayerView {
  id: string;
  name: string;
  kind: "human" | "ai";
  team: ArenaTeam;
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: -1 | 1;
  captured: boolean;
  connected: boolean;
  invulnerable: boolean;
}

export interface ArenaBubbleView {
  id: string;
  x: number;
  y: number;
  radius: number;
  team: ArenaTeam;
  trappedPlayerId?: string;
}

export interface ArenaPlatformView {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  oneWay: boolean;
}

export interface ArenaViewModel {
  id: string;
  mode: "1v1" | "2v2";
  phase: string;
  tick: number;
  round: number;
  roundTimeSeconds: number;
  roundWins: Record<ArenaTeam, number>;
  score: Record<ArenaTeam, number>;
  scoreTarget: number;
  players: ArenaPlayerView[];
  bubbles: ArenaBubbleView[];
  platforms: ArenaPlatformView[];
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord {
  return value !== null && typeof value === "object" ? (value as UnknownRecord) : {};
}

function array(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === "string" && value.length > 0 ? value : fallback;
}

function idValue(value: unknown, fallback: string): string {
  if (typeof value === "string" && value.length > 0) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return fallback;
}

function numberValue(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function normalizeTeam(value: unknown, index = 0): ArenaTeam {
  if (value === "B" || value === "ORANGE" || value === "orange" || value === 1) {
    return "B";
  }
  if (value === "A" || value === "BLUE" || value === "blue" || value === 0) {
    return "A";
  }
  return index % 2 === 0 ? "A" : "B";
}

function scoreValue(rawScore: UnknownRecord, team: ArenaTeam): number {
  const candidates = team === "A"
    ? [rawScore.A, rawScore.BLUE, rawScore.blue, rawScore.teamA]
    : [rawScore.B, rawScore.ORANGE, rawScore.orange, rawScore.teamB];

  for (const candidate of candidates) {
    if (typeof candidate === "number") {
      return candidate;
    }
  }
  return 0;
}

function playerPosition(raw: UnknownRecord, index: number): { x: number; y: number } {
  const position = record(raw.position);
  const body = record(raw.body);
  const bodyPosition = record(body.position);
  const fallbackColumns = [220, 1060, 380, 900];
  const fallbackX = fallbackColumns[index] ?? 640;
  return {
    x: numberValue(raw.x, numberValue(position.x, numberValue(bodyPosition.x, fallbackX))),
    y: numberValue(raw.y, numberValue(position.y, numberValue(bodyPosition.y, 580)))
  };
}

/**
 * The only simulation-to-renderer mapping. Keeping tolerant reads here lets the
 * greybox renderer stay disposable while the authoritative state remains typed
 * and serializable in the shared simulation package.
 */
export function toArenaViewModel(state: MatchState): ArenaViewModel {
  const raw = record(state);
  const slots = array(raw.slots).map(record);
  const playerSource = array(raw.players);
  const players = (playerSource.length > 0 ? playerSource : slots).map((item, index) => {
    const player = record(item);
    const slotId = stringValue(player.slotId, stringValue(player.id, `p${index + 1}`));
    const slot = slots.find((candidate) => stringValue(candidate.id, "") === slotId) ?? slots[index] ?? {};
    const velocity = record(player.velocity);
    const position = playerPosition(player, index);
    const connection = record(player.connection);
    const status = stringValue(player.status, "active");
    const kind = stringValue(slot.kind, stringValue(player.kind, "human"));

    return {
      id: slotId,
      name: stringValue(slot.name, stringValue(player.name, `P${index + 1}`)),
      kind: kind.toLowerCase() === "ai" || kind.toLowerCase() === "bot" ? "ai" : "human",
      team: normalizeTeam(slot.team ?? player.team, index),
      x: position.x,
      y: position.y,
      vx: numberValue(player.vx, numberValue(velocity.x, 0)),
      vy: numberValue(player.vy, numberValue(velocity.y, 0)),
      facing: numberValue(player.facing, 1) < 0 ? -1 : 1,
      captured: booleanValue(player.captured, status === "captured" || status === "trapped"),
      connected: booleanValue(
        player.connected,
        booleanValue(connection.connected, booleanValue(slot.connected, true))
      ),
      invulnerable: booleanValue(
        player.invulnerable,
        numberValue(player.invulnerabilityTicksRemaining, 0) > 0 || status === "respawning"
      )
    } satisfies ArenaPlayerView;
  });

  const bubbles = array(raw.bubbles ?? raw.projectiles).map((item, index) => {
    const bubble = record(item);
    const position = playerPosition(bubble, index);
    const trapped = bubble.trappedPlayerId ?? bubble.capturedPlayerId;
    const view: ArenaBubbleView = {
      id: idValue(bubble.id, `bubble-${index}`),
      x: position.x,
      y: position.y,
      radius: numberValue(bubble.radius, 28),
      team: normalizeTeam(bubble.team, index)
    };
    if (typeof trapped === "string" && trapped.length > 0) {
      view.trappedPlayerId = trapped;
    }
    return view;
  });

  const platforms = array(raw.platforms).map((item, index) => {
    const platform = record(item);
    return {
      id: idValue(platform.id, `platform-${index}`),
      x: numberValue(platform.x, 0),
      y: numberValue(platform.y, 0),
      width: numberValue(platform.width, 0),
      height: numberValue(platform.height, 22),
      oneWay: booleanValue(platform.oneWay, true)
    } satisfies ArenaPlatformView;
  });

  const rawScore = record(raw.scores ?? raw.score ?? raw.roundScore);
  const rawRoundWins = record(raw.roundWins ?? raw.roundsWon);
  const timer = numberValue(raw.roundTimeRemainingMs, numberValue(
    raw.timeRemainingMs,
    numberValue(raw.roundTicksRemaining, 75 * TICK_RATE) / TICK_RATE * 1000
  ));
  const modeValue = stringValue(raw.mode, players.length > 2 ? "2v2" : "1v1").toLowerCase();

  return {
    id: stringValue(raw.id, "foam-arena"),
    mode: modeValue === "team" || modeValue.includes("2") ? "2v2" : "1v1",
    phase: stringValue(raw.phase, stringValue(raw.status, "ROUND_ACTIVE")),
    tick: numberValue(raw.tick, 0),
    round: numberValue(raw.round, numberValue(raw.roundNumber, 1)),
    roundTimeSeconds: Math.max(0, timer / 1000),
    roundWins: {
      A: scoreValue(rawRoundWins, "A"),
      B: scoreValue(rawRoundWins, "B")
    },
    score: {
      A: scoreValue(rawScore, "A"),
      B: scoreValue(rawScore, "B")
    },
    scoreTarget: numberValue(raw.targetScore ?? raw.scoreTarget, modeValue === "team" || modeValue.includes("2") ? 5 : 3),
    players,
    bubbles,
    platforms
  };
}
